import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../config/firebase';


const VAPID_KEY = 'BHogT4jziXQKPW8sz3UGy0WK4nT46_CufxYrkOcp72uXqKx2FXZ8EidY41XHJnbiVGrU-Ytxo620_Qsqja0a0Ao';

class FCMService {
  constructor() {
    this.isSupported = typeof window !== 'undefined' && 
      'serviceWorker' in navigator && 
      Boolean(navigator.serviceWorker) && 
      'PushManager' in window &&
      Boolean(messaging);
    this.initializeServiceWorker();
  }

  async initializeServiceWorker() {
    if (!this.isSupported) return;
    
    try {
      const browserName = this.getBrowserName();
      console.log(`Initializing FCM with unified service worker for ${browserName}`);
      
      // Await unified service worker registration
      const readyRegistration = await navigator.serviceWorker.ready;
      console.log('Unified service worker is ready for FCM:', readyRegistration);
    } catch (error) {
      console.error('Error initializing FCM service worker:', error);
    }
  }


  async requestPermission() {
    if (!this.isSupported) {
      console.warn('Push notifications not supported in this browser');
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  async getToken() {
    if (!this.isSupported || !messaging) {
      console.warn('Push notifications not supported in this environment');
      return null;
    }
    
    try {
      const browserName = this.getBrowserName();
      console.log(`Getting FCM token for ${browserName}`);
      
      // Ensure service worker is ready before getting token
      const registration = await navigator.serviceWorker.ready;
      
      if (!registration) {
        console.error('Service worker not registered for FCM');
        return null;
      }
      
      console.log('Service worker registration found:', {
        scope: registration.scope,
        active: !!registration.active,
        state: registration.active?.state
      });
      
      const token = await getToken(messaging, { 
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration
      });
      
      if (token) {
        console.log(`FCM Token generated for ${browserName}:`, token);
        
        // Chrome-specific debugging
        if (browserName === 'Chrome') {
          console.log('Chrome FCM Debug:', {
            tokenLength: token.length,
            vapidKey: VAPID_KEY.substring(0, 20) + '...',
            registrationScope: registration.scope,
            notificationPermission: Notification.permission
          });
        }
      } else {
        console.warn(`No FCM token generated for ${browserName}`);
      }
      
      return token;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      console.error('Browser:', this.getBrowserName());
      console.error('Notification permission:', Notification.permission);
      console.error('Error details:', error.message, error.stack);
      return null;
    }
  }

  onForegroundMessage(callback) {
    if (!this.isSupported || !messaging) return () => {};
    
    return onMessage(messaging, async (payload) => {
      console.log('Foreground message received:', payload);
      
      // Show native notification + keep in-app handling
      if (payload.notification) {
        try {
          await this.showNotification(payload.notification, payload.data || {});
        } catch (e) {
          console.warn('Failed to show native notification in foreground:', e);
        }
      }
      
      callback(payload);
    });
  }

  async showNotification(notification, data = {}) {
    if (!this.isSupported || Notification.permission !== 'granted') return;

    const title = notification?.title || 'Misas App';
    const options = {
      body: notification?.body || 'You have a new notification',
      icon: notification?.icon || '/logo192.png',
      badge: '/logo192.png',
      tag: 'misas-notification',
      requireInteraction: true,
      actions: [
        { action: 'open', title: 'Open App', icon: '/logo192.png' },
        { action: 'close', title: 'Close', icon: '/logo192.png' }
      ],
      data: {
        url: data?.url || '/homedashboard',
        ...data
      }
    };

    try {
      // Prefer service worker registration to ensure click actions work
      const registration = await navigator.serviceWorker.ready;
      if (registration?.showNotification) {
        registration.showNotification(title, options);
        return;
      }
    } catch (error) {
      console.warn('Service worker registration not available, falling back to Notification API:', error);
    }

    // Fallback to Notification API directly
    try {
      new Notification(title, options);
    } catch (error) {
      console.error('Failed to display notification:', error);
    }
  }

  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    return {
      device_type: 'web',
      device_name: isMobile ? 'Mobile Browser' : 'Desktop Browser',
      app_version: '1.0.0',
      os_version: navigator.platform,
      browser: this.getBrowserName(),
      user_agent: userAgent
    };
  }

  getBrowserName() {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    
    return 'Unknown';
  }

  getPermissionStatus() {
    if (!this.isSupported) return 'not-supported';
    return Notification.permission;
  }

  async getServiceWorkerStatus() {
    if (!this.isSupported) return { supported: false };
    
    try {
      const registration = await navigator.serviceWorker.ready;
      return {
        supported: true,
        registered: !!registration,
        active: !!registration?.active,
        scope: registration?.scope,
        state: registration?.active?.state
      };
    } catch (error) {
      return {
        supported: true,
        registered: false,
        error: error.message
      };
    }
  }
}

export default new FCMService();